import { Store } from "vuex";
import getCacheKey from "@/vue/planning/logic/getCacheKey";
import responsesCache from "@/vue/planning/state/responsesCache";
import { getAvailRequestData } from "@/vue/planning/logic/getRequestData";
import { AvailabilitiesResponse, AvailablePackage, PrestationRaw } from "@/types/responses";
import * as _ from "lodash";
import { getUniqueId } from "@/vue/planning/logic/uniqueId";
import isHaveAvailability from "@/vue/planning/logic/isHaveAvailability";
import { checkIsSummerOrWinter } from "@/utils/utils";
import { PrestationData } from "@/types";
import { getAvailablePackagesWiyp } from "./availablesPackagesWiyp";
import flightPackCode from "@/config/flightPackCode";
import transportPackCode from "@/config/transportPackCode";
import { trainStationCodeMap } from "@/config/trainStationCodeMap";
import { airportCodeMap } from "@/config/airportCodeMap";
import Cookies from "js-cookie";

const syncPackAvailabilitiesToStore = (
  availResp: AvailabilitiesResponse,
  packCode: string,
  storeObj: Store<any>
): AvailabilitiesResponse => {
  // 1️⃣  Ensure every prestation has a deterministic ID
  if (!availResp.prestations)
    throw new Error("prestations are required in availResp.prestations");

  availResp.prestations = availResp.prestations.map((prest: any) => {
    prest["id"] = getUniqueId(prest);
    return prest;
  });

  // 2️⃣  Build a `{ [id]: prest }` map for the given packCode
  const newMapObj: Record<string, any> = {
    ...storeObj.state.prestMaps.seAvailabilities,
    [packCode]: {},
  };

  availResp.prestations.forEach(
    (prest: any) => (newMapObj[packCode][prest.id] = prest)
  );

  // 3️⃣  Sync both maps in the store
  storeObj.state.prestMaps.seAvailabilities = newMapObj;
  storeObj.state.prestMaps.prices = newMapObj;

  // Here we must adjust wiyp data with this
  // ex. newMapObj:  codepackage PFTT - prestId 800371 - prestIdFusion/prestIdSource 185330
  // Iterate through newMapObj (which has one key: codePackage, e.g., 'PFTT')
  // On the last level we have objects like {id: ..., prestId, prestIdFusion, ...}
  // Ex. ['PFTT-874568-185330-185330', 'PFTT-800371-185330-185330', 'PFTT-800371-812705-812705', 'PFTT-800371-185331-185331']
  // Create a map with codePackage as key and array of prestation property objects as value
  const packagePrestMap = new Map<string, Array<{ prestId: string, prestIdSource: string, prestIdFusion: string }>>();

  availResp.prestations.forEach((prest: any) => {
    if (!packagePrestMap.has(prest.codePackage)) {
      packagePrestMap.set(prest.codePackage, []);
    }
    packagePrestMap.get(prest.codePackage)!.push({
      prestId: prest.prestId,
      prestIdSource: prest.prestIdSource,
      prestIdFusion: prest.prestIdFusion
    });
  });

  // Object.entries(newMapObj).forEach(([codePackage, level2]) => {
  //   packagePrestMap.set(codePackage, Object.values(level2).map((prest: any) => ({
  //     prestId: prest.prestId,
  //     prestIdSource: prest.prestIdSource,
  //     prestIdFusion: prest.prestIdFusion
  //   })));
  // });
  storeObj.dispatch("updateWiypCallWithAvailability", packagePrestMap);


  return availResp;
};

const updateDisplayedPrestations = async (storeObj: Store<any>, existingAvailabilities: AvailabilitiesResponse) => {
    const prestationsOnPage = window.lihe?.prestations || [];

    // Create a Set of prestationIds already on the page
    const pagePrestationIds = new Set(prestationsOnPage.map((p: PrestationData) => p.prestationId));

    // Filter out prestations that are already on the page
    const existingAvailabilitiesNotInPage = existingAvailabilities.prestations!.filter(
      (prest) => !pagePrestationIds.has(prest.prestIdFusion)
    );

    if(existingAvailabilitiesNotInPage.length === 0) return

    const prestFromCatalogPromises = existingAvailabilitiesNotInPage.map(prest => {
      const requestData = {
        locale: storeObj.getters.locale,
        prestationId: prest.prestId,
      };
      return window.catalogService.getPrestationByLocale(requestData); // Returns a promise
    });
    
    const results = await Promise.all(prestFromCatalogPromises);
    
    storeObj.dispatch("updateInitialPrestations", results)
}

/**
 * Filter out available packages and prestations that have bad combinations of codePackage and origins
 * Example: a flight package with a train origin will be filtered out
 * @param avail - Array of available packages or prestations
 * @returns Array of available packages or prestations that have valid combinations of codePackage and origins
 */
const filterAvailablePackagesForBadCombinations = (avail: Array<AvailablePackage> | Array<PrestationRaw>, storeObj: Store<any>) => {
  if (avail.length > 0) {
    const filtered = avail.filter((packObj: any) => {

      // First we filter out CSE packages for NL & FR to avoid PFTTF to NL or PFTTNL to FR
      if (storeObj.state.language === "fr-FR-CSE") {
        const lang = Cookies.get('companyLang') ?? 'fr'
        if (lang === 'nl' && ['PFTTF', 'PFPTTF', 'PFPTT'].includes(packObj.codePackage)) {
          return false;
        }
        else if (['PFTTNL', 'PFPTTNL'].includes(packObj.codePackage)) {
          return false;
        }
      }
      
      const isFlightPackage = flightPackCode.includes(packObj.codePackage);
      const isTrainPackage = transportPackCode.includes(packObj.codePackage);
      const possibleOrigins: string[] | undefined = packObj.origins;

      if (isFlightPackage || isTrainPackage) {
        
        // Intersect possibleOrigins with respective code map depending on transport type
        if (isFlightPackage) {
          // If no origins are provided for a flight package, filter it out
          if (!possibleOrigins || !Array.isArray(possibleOrigins) || possibleOrigins.length === 0) {
            return false;
          }
          packObj.origins = possibleOrigins.filter(origin => airportCodeMap.has(origin));
          if (!packObj.origins || packObj.origins.length === 0) {
            return false; // No valid flight origins
          }
        }

        // Special logic for train : allow if no origins are provided
        if (isTrainPackage) {
          if (!possibleOrigins || !Array.isArray(possibleOrigins) || possibleOrigins.length === 0) {
            return true;
          }
          packObj.origins = possibleOrigins.filter(origin => trainStationCodeMap.has(origin));
          if (!packObj.origins || packObj.origins.length === 0) {
            return false; // No valid train origins if origins are provided
          }
        }
      }
      return true;
    });
    return filtered;
  }
  return avail;
}

const getAvailability = async (
  packCode: string,
  startDate: string,
  endDate: string,
  storeObj: Store<any>
): Promise<AvailabilitiesResponse | null> => {
  if (storeObj.state.datepickerStartDate) {
    const newSeason = checkIsSummerOrWinter(storeObj.state.datepickerStartDate);
    if (newSeason !== storeObj.state.season) {
      storeObj.state.season = newSeason;

      window.seasonManager.switchSeasonWithoutReloading();
    }
  }
  // try to extract cached response
  const cacheKey = getCacheKey(
    packCode,
    startDate,
    endDate,
    storeObj.state.season,
    storeObj.state.participants,
    storeObj.state.skipass
  );

  const existingAvailabilities = responsesCache.value.getResponse(
    "availabilities",
    cacheKey
  ) as AvailabilitiesResponse;
  
  if(existingAvailabilities && existingAvailabilities.prestations) {
    updateDisplayedPrestations(storeObj, existingAvailabilities)
    return syncPackAvailabilitiesToStore(existingAvailabilities, packCode, storeObj);
  }

  const requestData = getAvailRequestData(
    storeObj.state,
    storeObj.getters,
    packCode
  );

  const avail_resp: AvailabilitiesResponse =
    await window.searchEngineService.getRelatedPackages(
      requestData,
      "getAvailabilities"
    );
  // We filter all bad codepackages/origin combinations
  avail_resp.availablePackages = filterAvailablePackagesForBadCombinations(avail_resp.availablePackages || [], storeObj);
  avail_resp.prestations = filterAvailablePackagesForBadCombinations(avail_resp.prestations || [], storeObj);
  storeObj.state.availablePackageCodeWiyp = getAvailablePackagesWiyp(avail_resp);

  if (!isHaveAvailability(avail_resp)) {
    return null;
  }

  const packageId = avail_resp?.availablePackages?.find((packObj: any) => {
    return packObj.codePackage === packCode;
  })?.prestId;

  storeObj.commit("setPackageId", packageId);

  if (!avail_resp.prestations)
    throw new Error(
      "No prestations was received from the `availabilities` endpoint"
    );

  syncPackAvailabilitiesToStore(avail_resp, packCode, storeObj);

  responsesCache.value.setResponse("availabilities", cacheKey, avail_resp);
  responsesCache.value = _.cloneDeep(responsesCache.value);

  return avail_resp;
};

export default getAvailability;
